import type { ManifestField, ManifestSection, TemplateManifest } from './types';

// ── Field-level validation ───────────────────────────────────────────

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Validate a single manifest field value.
 * Returns an error message or empty string if valid.
 */
export function validateField(field: ManifestField, value: unknown): string {
  const strValue = typeof value === 'string' ? value.trim() : '';

  if (field.required) {
    if (field.type === 'regulation-picker') {
      if (!Array.isArray(value) || value.length === 0) {
        return `${field.label} is required.`;
      }
    } else if (!strValue) {
      return `${field.label} is required.`;
    }
  }

  // Skip further validation if empty and not required
  if (!strValue && field.type !== 'regulation-picker') {
    return '';
  }

  if (field.type === 'uuid' && strValue && !UUID_RE.test(strValue)) {
    return `${field.label} must be a valid UUID.`;
  }

  if (field.type === 'email' && strValue && !EMAIL_RE.test(strValue)) {
    return `${field.label} must be a valid email address.`;
  }

  if (field.type === 'number' && strValue && Number.isNaN(Number(strValue))) {
    return `${field.label} must be a number.`;
  }

  if (field.pattern && strValue) {
    const regex = new RegExp(field.pattern);
    if (!regex.test(strValue)) {
      return (
        field.patternError ||
        `${field.label} does not match the required format.`
      );
    }
  }

  if (field.minLength !== undefined && strValue.length < field.minLength) {
    return `${field.label} must be at least ${field.minLength} characters.`;
  }

  if (field.maxLength !== undefined && strValue.length > field.maxLength) {
    return `${field.label} must be at most ${field.maxLength} characters.`;
  }

  return '';
}

// ── Section-level validation ─────────────────────────────────────────

/**
 * Validate all fields in a section against the current form values.
 * Returns the first error found, or empty string if all valid.
 */
export function validateSection(
  section: ManifestSection,
  values: Record<string, unknown>,
): string {
  for (const field of section.fields) {
    const error = validateField(field, values[field.name]);
    if (error) {
      return error;
    }
  }

  return '';
}

/**
 * Validate all sections referenced by a phase.
 * Returns the first error found, or empty string if all valid.
 */
export function validatePhase(
  phaseKey: 'identity' | 'configure' | 'requirements' | 'review',
  manifest: TemplateManifest,
  values: Record<string, unknown>,
): string {
  const phase = manifest.spec.phases[phaseKey];
  if (!phase.sections) {
    return '';
  }

  for (const sectionRef of phase.sections) {
    // Only validate custom sections (core sections have their own logic)
    if (!sectionRef.ref.startsWith('custom/')) {
      continue;
    }

    const section = manifest.spec.sections[sectionRef.ref];
    if (!section) {
      continue;
    }

    const error = validateSection(section, values);
    if (error) {
      return error;
    }
  }

  return '';
}

// ── Manifest structural validation ───────────────────────────────────

/**
 * Validate that a parsed manifest object has the minimum required structure.
 * Returns an array of issues (empty if valid).
 */
export function validateManifestStructure(manifest: unknown): string[] {
  const issues: string[] = [];

  if (!manifest || typeof manifest !== 'object') {
    return ['Manifest must be a YAML object.'];
  }

  const m = manifest as Record<string, unknown>;

  if (m.apiVersion !== 'vivicta.io/v1') {
    issues.push(
      `Unsupported apiVersion: ${String(
        m.apiVersion,
      )}. Expected vivicta.io/v1.`,
    );
  }

  if (m.kind !== 'TemplateManifest') {
    issues.push(
      `Unexpected kind: ${String(m.kind)}. Expected TemplateManifest.`,
    );
  }

  const metadata = m.metadata as Record<string, unknown> | undefined;
  if (!metadata?.name) {
    issues.push('metadata.name is required.');
  }
  if (!metadata?.title) {
    issues.push('metadata.title is required.');
  }
  if (!metadata?.description) {
    issues.push('metadata.description is required.');
  }

  const spec = m.spec as Record<string, unknown> | undefined;
  if (!spec) {
    issues.push('spec is required.');
  } else {
    if (!spec.phases || typeof spec.phases !== 'object') {
      issues.push('spec.phases is required.');
    }
    if (!spec.sections || typeof spec.sections !== 'object') {
      issues.push('spec.sections is required.');
    }
  }

  return issues;
}

// ── Default value extraction ─────────────────────────────────────────

/**
 * Build a default form values object from a manifest.
 * Merges field-level defaults with spec.defaults (spec.defaults wins).
 */
export function extractDefaults(
  manifest: TemplateManifest,
): Record<string, unknown> {
  const defaults: Record<string, unknown> = {};

  // Collect field-level defaults from all sections
  for (const section of Object.values(manifest.spec.sections)) {
    for (const field of section.fields) {
      if (field.default !== undefined) {
        defaults[field.name] = field.default;
      }
    }
  }

  // Overlay spec-level defaults (take precedence)
  if (manifest.spec.defaults) {
    Object.assign(defaults, manifest.spec.defaults);
  }

  return defaults;
}
